# Data Sources & Attribution

**Last Updated:** January 15, 2026

FHIRfly provides access to healthcare reference data sourced from official government and standards organization databases. This page describes our data sources, their licensing terms, and attribution requirements.

---

## Overview

All data provided through FHIRfly APIs is sourced from publicly available datasets maintained by government agencies and healthcare standards organizations. This data is reference information, not Protected Health Information (PHI).

**Important Disclaimer:** FHIRfly is not affiliated with, endorsed by, or officially connected to the U.S. Food and Drug Administration (FDA), Centers for Medicare & Medicaid Services (CMS), National Library of Medicine (NLM), Centers for Disease Control and Prevention (CDC), Regenstrief Institute, National Uniform Claim Committee (NUCC), or any other data source organization. We provide access to publicly available data from these sources.

---

## Data Sources

### National Drug Code (NDC)

| Attribute | Details |
|-----------|---------|
| **Source** | U.S. Food and Drug Administration (FDA) |
| **Dataset** | NDC Directory |
| **URL** | https://www.fda.gov/drugs/drug-approvals-and-databases/national-drug-code-directory |
| **License** | Public Domain (U.S. Government Work) |
| **Update Frequency** | Daily |
| **Records** | ~137,000 products, ~259,000 packages |

**Attribution:** Data from the FDA National Drug Code Directory.

---

### National Provider Identifier (NPI)

| Attribute | Details |
|-----------|---------|
| **Source** | Centers for Medicare & Medicaid Services (CMS) |
| **Dataset** | NPPES NPI Registry |
| **URL** | https://npiregistry.cms.hhs.gov/ |
| **License** | Public Domain (U.S. Government Work) |
| **Update Frequency** | Weekly |
| **Records** | ~9.3 million providers |

**Attribution:** Data from the CMS National Plan and Provider Enumeration System (NPPES).

---

### NUCC Taxonomy

| Attribute | Details |
|-----------|---------|
| **Source** | National Uniform Claim Committee (NUCC) |
| **Dataset** | Health Care Provider Taxonomy Code Set |
| **URL** | https://nucc.org/index.php/code-sets-mainmenu-41/provider-taxonomy-mainmenu-40 |
| **License** | Free for use with attribution |
| **Update Frequency** | Semi-annually |
| **Records** | ~900 taxonomy codes |

**Attribution:** Provider taxonomy codes from the National Uniform Claim Committee (NUCC).

---

### RxNorm

| Attribute | Details |
|-----------|---------|
| **Source** | National Library of Medicine (NLM) |
| **Dataset** | RxNorm |
| **URL** | https://www.nlm.nih.gov/research/umls/rxnorm/index.html |
| **License** | UMLS License (free registration required for direct access) |
| **Update Frequency** | Weekly |
| **Records** | ~228,000 drug concepts |

**Attribution:** RxNorm is produced by the National Library of Medicine.

**Licensing Note:** FHIRfly accesses RxNorm under the UMLS Metathesaurus License. Your use of RxNorm data through the FHIRfly API is subject to these Terms of Service. If you require direct access to RxNorm data files or wish to redistribute RxNorm data outside of FHIRfly, you may need to obtain your own UMLS license from NLM.

---

### LOINC

| Attribute | Details |
|-----------|---------|
| **Source** | Regenstrief Institute |
| **Dataset** | Logical Observation Identifiers Names and Codes (LOINC) |
| **URL** | https://loinc.org/ |
| **License** | LOINC License (free, requires attribution) |
| **Update Frequency** | Semi-annually |
| **Records** | ~108,000 codes |

**Attribution Requirements:** Per the LOINC License:

> "LOINC codes, LOINC Table, LOINC Table Core, LOINC Panels and Forms File, LOINC Answer File, LOINC Part File, LOINC Group File, LOINC Document Ontology File, LOINC Hierarchies, LOINC Linguistic Variants, LOINC/RSNA Radiology Playbook, LOINC/IEEE Medical Device Code Mapping Table, and LOINC Display Name File are copyright © 1995-2024, Regenstrief Institute, Inc. All rights reserved."

**Note:** FHIRfly is licensed to provide LOINC data through our API.

---

### ICD-10-CM / ICD-10-PCS (U.S.)

| Attribute | Details |
|-----------|---------|
| **Source** | Centers for Medicare & Medicaid Services (CMS) / CDC |
| **Dataset** | ICD-10-CM (Clinical Modification - Diagnoses), ICD-10-PCS (Procedure Coding System) |
| **URL** | https://www.cms.gov/medicare/coding-billing/icd-10-codes |
| **License** | Public Domain (U.S. Government Work) |
| **Update Frequency** | Annually (October 1) |
| **Records** | ~47,000 CM codes, ~79,000 PCS codes |

**Attribution:** ICD-10-CM/PCS codes from the Centers for Medicare & Medicaid Services.

**Note:** FHIRfly provides the U.S. Clinical Modification (ICD-10-CM) and Procedure Coding System (ICD-10-PCS), which are maintained by CMS for use in the United States. This is distinct from the WHO's ICD-10 classification, which has different code structures and is used internationally.

---

### CVX (Vaccine Codes)

| Attribute | Details |
|-----------|---------|
| **Source** | Centers for Disease Control and Prevention (CDC) |
| **Dataset** | CVX Code Set |
| **URL** | https://www2a.cdc.gov/vaccines/iis/iisstandards/vaccines.asp?rpt=cvx |
| **License** | Public Domain (U.S. Government Work) |
| **Update Frequency** | As needed |
| **Records** | ~288 codes |

**Attribution:** CVX vaccine codes from the Centers for Disease Control and Prevention.

---

### MVX (Vaccine Manufacturers)

| Attribute | Details |
|-----------|---------|
| **Source** | Centers for Disease Control and Prevention (CDC) |
| **Dataset** | MVX Code Set |
| **URL** | https://www2a.cdc.gov/vaccines/iis/iisstandards/vaccines.asp?rpt=mvx |
| **License** | Public Domain (U.S. Government Work) |
| **Update Frequency** | As needed |
| **Records** | ~89 codes |

**Attribution:** MVX manufacturer codes from the Centers for Disease Control and Prevention.

---

### FDA Drug Labels (SPL)

| Attribute | Details |
|-----------|---------|
| **Source** | U.S. Food and Drug Administration (FDA) |
| **Dataset** | Structured Product Labeling (SPL) |
| **URL** | https://dailymed.nlm.nih.gov/dailymed/ |
| **License** | Public Domain (U.S. Government Work) |
| **Update Frequency** | Daily |
| **Records** | ~257,000 labels |

**Attribution:** Drug labeling data from FDA DailyMed via the National Library of Medicine.

---

### SMA Endpoint Directory

| Attribute | Details |
|-----------|---------|
| **Source** | Centers for Medicare & Medicaid Services (CMS) |
| **Dataset** | State Medicaid Agency Endpoint Directory |
| **URL** | https://github.com/CMSgov/SMA-Endpoint-Directory |
| **License** | Public Domain (U.S. Government Work) |
| **Update Frequency** | Monthly (survey-based) |
| **Records** | 56 states and territories |

**Attribution:** SMA Endpoint Directory data from the Centers for Medicare & Medicaid Services.

---

### HCC Crosswalk (CMS Risk Adjustment)

| Attribute | Details |
|-----------|---------|
| **Source** | Centers for Medicare & Medicaid Services (CMS) |
| **Dataset** | HCC Risk Adjustment Model Crosswalk |
| **URL** | https://www.cms.gov/medicare/payment/medicare-advantage-rates-statistics/risk-adjustment |
| **License** | Public Domain (U.S. Government Work) |
| **Update Frequency** | Annually |
| **Records** | ~28,281 mappings |

**Attribution:** CMS HCC Risk Adjustment Model. CMS.gov.

---

### OPCS-4 (UK Procedure Classification)

| Attribute | Details |
|-----------|---------|
| **Source** | NHS England (via TRUD) |
| **Dataset** | OPCS Classification of Interventions and Procedures, Version 4.11 |
| **URL** | https://isd.digital.nhs.uk/trud/ |
| **License** | Open Government Licence v3.0 (Crown copyright) |
| **Update Frequency** | Periodically |
| **Records** | ~1,665 codes |

**Attribution:** The OPCS Classification of Interventions and Procedures, Crown copyright.

---

### dm+d (UK Dictionary of Medicines and Devices)

| Attribute | Details |
|-----------|---------|
| **Source** | NHS England / NHSBSA (via TRUD) |
| **Dataset** | Dictionary of Medicines and Devices (dm+d) |
| **URL** | https://isd.digital.nhs.uk/trud/ |
| **License** | Open Government Licence v3.0 (Crown copyright) |
| **Update Frequency** | Weekly |
| **Records** | ~415,000 concepts (VTM, VMP, AMP, VMPP, AMPP) |

**Attribution:** dm+d, NHS England / NHSBSA. Crown copyright. Open Government Licence v3.0.

---

### DDI Reference (Drug-Drug Interactions)

| Attribute | Details |
|-----------|---------|
| **Source** | U.S. Food and Drug Administration (FDA) + National Library of Medicine (NLM) |
| **Dataset** | FDA Structured Product Labeling (SPL) drug interaction sections + RxNorm drug enrichment |
| **URL** | https://dailymed.nlm.nih.gov/dailymed/ and https://www.nlm.nih.gov/research/umls/rxnorm/ |
| **License** | Public Domain (U.S. Government Work) |
| **Update Frequency** | Daily (FDA labels), Weekly (RxNorm) |

**Attribution:** Drug interaction data sourced from FDA-approved labeling via DailyMed and NLM RxNorm.

**Note:** DDI Reference surfaces FDA-approved interaction text (drug_interactions, warnings, contraindications sections) enriched with RxNorm drug classification data. This is reference information and does not constitute medical advice.

---

## Attribution Guidelines

### When to Attribute

You should provide attribution when:

- Displaying data publicly on websites or in publications
- Redistributing data to third parties
- Creating derivative works from the data

### Examples of "Public Display"

| Scenario | Attribution Needed? |
|----------|---------------------|
| Public website showing drug information to users | Yes |
| Published research paper citing lookup results | Yes |
| Commercial product documentation | Yes |
| Marketing materials mentioning data sources | Yes |
| Internal clinical ops dashboard (authenticated users only) | Recommended but not required |
| Backend API processing without user-visible display | No |
| Automated data validation pipelines | No |

### When Attribution is Not Required

- Internal use within your organization
- Automated processing without public display
- API integrations where data is used programmatically
- Development and testing environments

### Suggested Attribution Format

For general use:

> "Healthcare reference data provided by FHIRfly (fhirfly.io), sourced from FDA, CMS, NLM, CDC, Regenstrief Institute, and NUCC."

For specific datasets, use the attribution text provided in each section above.

For LOINC data specifically, you must include the copyright notice from the LOINC License when redistributing.

---

## Data Accuracy and Timeliness

### Update Schedule

| Dataset | Update Frequency | Typical Lag |
|---------|------------------|-------------|
| NDC | Daily | < 24 hours |
| NPI | Weekly | < 7 days |
| RxNorm | Weekly | < 7 days |
| LOINC | Semi-annually | < 30 days |
| ICD-10 | Annually | < 30 days |
| CVX/MVX | As needed | < 7 days |
| FDA Labels | Daily | < 24 hours |
| HCC Crosswalk | Annually | < 30 days |
| OPCS-4 | Periodically | < 30 days |
| dm+d | Weekly | < 7 days |
| DDI Reference | Daily / Weekly | < 24 hours |

### Accuracy Disclaimer

While we strive to provide accurate and timely data:

- Source databases may contain errors
- There may be delays between source updates and our API
- Historical or deprecated codes may not be included
- You should validate data for critical use cases

**FHIRfly provides reference data for informational purposes. Always verify critical information with authoritative sources and consult qualified professionals for medical decisions.**

---

## Licensing Summary

FHIRfly provides access to these data sources under their applicable licenses. **You are responsible for compliance with all underlying licenses when you use data obtained through FHIRfly in your own products or services.** The table below summarizes the general licensing terms:

| Dataset | License Type | Commercial Use | Redistribution |
|---------|--------------|----------------|----------------|
| NDC | Public Domain | Yes | Yes |
| NPI | Public Domain | Yes | Yes |
| NUCC | Free with Attribution | Yes | Yes |
| RxNorm | UMLS (via FHIRfly) | Yes | Via FHIRfly only |
| LOINC | LOINC License | Yes | With attribution |
| ICD-10 | Public Domain | Yes | Yes |
| CVX/MVX | Public Domain | Yes | Yes |
| FDA Labels | Public Domain | Yes | Yes |
| HCC Crosswalk | Public Domain | Yes | Yes |
| OPCS-4 | Open Government Licence v3.0 | Yes | With attribution |
| dm+d | Open Government Licence v3.0 | Yes | With attribution |
| DDI Reference | Public Domain | Yes | Yes |

---

## Questions

For questions about data sources or licensing, contact us:

**FHIRfly.io LLC**
Email: admin@fhirfly.io
Phone: (816) 552-2628
